import { Box, makeStyles, Typography } from '@material-ui/core';
import { ShortcutLink, ShortcutSection, ViewMode } from '../../types';
import { ShortcutSectionItem } from '../atoms/';

type Props = {
  searchQuery: string;
  filteredSections: ShortcutSection[];
  favoriteIds: string[];
  toggleFavorite: (link: ShortcutLink) => void;
  onEditCustomLink?: (link: ShortcutLink) => void;
  onDeleteCustomLink?: (link: ShortcutLink) => void;
  actionsDisabled?: boolean;
  viewMode: ViewMode;
};

export const RenderSections = ({
  searchQuery,
  filteredSections,
  favoriteIds,
  toggleFavorite,
  viewMode,
  onEditCustomLink,
  onDeleteCustomLink,
  actionsDisabled = false,
}: Props) => {
  const classes = useStyles();
  const favoriteIdSet = new Set(favoriteIds);

  const hasSearchQuery = searchQuery.length > 0;
  const hasAnySearchResults = filteredSections.length > 0;
  const shouldShowGlobalSearchEmpty = hasSearchQuery && !hasAnySearchResults;

  const renderItems = (section: ShortcutSection) => {
    const links = section.links;

    if (links.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyStateTitle}>
            {section.id === 'engineering'
              ? 'Engineering shortcuts are coming soon.'
              : 'No shortcuts available in this section.'}
          </Typography>
          <Typography className={classes.emptyStateBody}>
            {section.id === 'engineering'
              ? 'This space is reserved for upcoming engineering tools. Use other sections for now.'
              : 'Try another category or update your search to discover more shortcuts.'}
          </Typography>
        </Box>
      );
    }
    const isListMode = viewMode === ViewMode.LIST;

    return (
      <Box className={isListMode ? classes.list : classes.grid}>
        {links.map(link => (
          <ShortcutSectionItem
            key={link.id}
            link={link}
            isFavorite={favoriteIdSet.has(link.id)}
            toggleFavorite={toggleFavorite}
            onEdit={link.isCustom ? onEditCustomLink : undefined}
            onDelete={link.isCustom ? onDeleteCustomLink : undefined}
            actionsDisabled={actionsDisabled}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      {shouldShowGlobalSearchEmpty ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyStateTitle}>
            No shortcuts found for "{searchQuery}".
          </Typography>
          <Typography className={classes.emptyStateBody}>
            Try another keyword, switch category, or clear your search to see
            all available shortcuts.
          </Typography>
        </Box>
      ) : (
        filteredSections.map(section => {
          const SectionIcon = section.icon;
          return (
            <Box className={classes.section} key={section.id}>
              <Box className={classes.sectionHeader}>
                <Box className={classes.sectionTitleWrap}>
                  <SectionIcon className={classes.sectionIcon} />
                  <Typography variant="h4" className={classes.sectionTitle}>
                    {section.title}
                  </Typography>
                </Box>
                <Typography className={classes.sectionMeta}>
                  {section.links.length > 0
                    ? `${section.links.length} shortcuts`
                    : 'No items'}
                </Typography>
              </Box>

              {renderItems(section)}
            </Box>
          );
        })
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  list: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  sectionTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  sectionIcon: {
    color: theme.palette.secondary.main,
    flexShrink: 0,
  },
  sectionTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: '1.8rem',
    lineHeight: 1.25,
  },
  sectionMeta: {
    color: theme.palette.textSubtle,
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  emptyState: {
    border: `1px dashed ${theme.palette.border}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  emptyStateTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  emptyStateBody: {
    fontSize: '0.88rem',
    lineHeight: 1.45,
  },
}));
